import React, { useState } from 'react';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';
import { ISlide } from '@/components/admin/SlideShow/stores/useSlideShowStore';
import useSlideShowContext from '@/components/admin/SlideShow/stores/useSlideShowContext';
import { SlideGroup } from '@/components/admin/SlideShow/Builder/CreateTransistionGroup/utils';
import AddSlide from '../../Utils/AddSlide';
import builderStyles from '../SlideShowBuilder.module.scss';
import SlideGroupCard from './SlideGroupCard';
import SlideThumbnail from './SlideThumbnail';
import useSlideReorder from './useSlideReorder';
import { DisplayItem, isTransition, makeDraggableId, parseDraggableId } from './slideReorder';

interface SlidePanelProps {
  setIsSaving: (saving: boolean) => void;
  setSelectedSlide: (slide: DisplayItem) => void;
}

const FINAL_CARD_ID = '0';

// Slides and group cards share one Droppable. Only slides drag; the cards are disabled Draggables so
// react-beautiful-dnd's indexes stay contiguous. Slide groups are reordered from the Group Slides popup.
const SlidePanel = ({ setIsSaving, setSelectedSlide }: SlidePanelProps) => {
  const displayItems = useSlideShowContext(state => state.slides) as DisplayItem[];
  const selectedSlide = useSlideShowContext(state => state.currentSlide);
  const reorderSlide = useSlideReorder({ setIsSaving });
  const [isDragging, setIsDragging] = useState(false);

  const transitionCards = displayItems.filter(isTransition) as SlideGroup[];

  const handleDragEnd = (result: DropResult) => {
    setIsDragging(false);
    if (!result.destination) return;

    const { id } = parseDraggableId(result.draggableId);
    reorderSlide(id, result.destination.index);
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd} onDragStart={() => setIsDragging(true)}>
      <section
        aria-label="Slide Panel"
        className={builderStyles.slidePanel}
        data-drag-active={isDragging || undefined}
        id="slide-panel"
      >
        <Droppable droppableId="slides">
          {provided => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {displayItems.map((item, arrayIndex) => {
                const isSelected = !!selectedSlide && String(selectedSlide.id) === String(item.id);
                // Draggable indexes are 1-based so a drop's destination index equals its display position.
                const index = arrayIndex + 1;

                if (isTransition(item)) {
                  const card = item as SlideGroup;
                  const isFinal = String(card.id) === FINAL_CARD_ID;

                  return (
                    <SlideGroupCard
                      key={makeDraggableId('group', card.id)}
                      card={card}
                      groupIndex={transitionCards.indexOf(card)}
                      index={index}
                      isFinal={isFinal}
                      isSelected={isSelected}
                      transitionCards={transitionCards}
                      onSelect={() => setSelectedSlide(item)}
                    />
                  );
                }

                return (
                  <SlideThumbnail
                    key={makeDraggableId('slide', item.id)}
                    index={index}
                    isSelected={isSelected}
                    slide={item as ISlide}
                    onSelect={() => setSelectedSlide(item)}
                  />
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
        <AddSlide styling="slide" />
      </section>
    </DragDropContext>
  );
};

export default SlidePanel;
